import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../Button/Button.js';
import { Icon } from '../Icon/Icon.js';
import { Input } from '../Input/Input.js';
import { DropdownMenu } from '../DropdownMenu/DropdownMenu.js';
import './FolderHeader.css';

export const FolderHeader = ({
  folderName = 'Folder name',
  isExpanded = true,
  isEditing = false,
  parentPathNames = [],
  onToggleExpanded,
  onNameChange,
  onAddColor,
  onAddFolder,
  onStartEditing,
  onStopEditing,
  onExpandCollapse,
  onRename,
  onMove,
  onMoveUp,
  onMoveDown,
  onDelete,
  onDuplicate,
  className = '',
  variant = 'default',
  ...props
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const moreButtonRef = useRef(null);
  const inputRef = useRef(null);

  // Manual state management for folder name editing to avoid sync issues
  const [editingName, setEditingName] = useState(folderName);
  const lastCommittedFolderNameRef = useRef(folderName);
  const isExplicitlyCancelingFolderRef = useRef(false);
  
  // Update local value when prop changes (but not during editing)
  useEffect(() => {
    if (!isEditing && folderName !== lastCommittedFolderNameRef.current) {
      setEditingName(folderName);
      lastCommittedFolderNameRef.current = folderName;
    }
  }, [folderName, isEditing]);
  
  const startNameEditing = () => {
    // This is handled by the parent component
  };
  
  const updateNameValue = (newValue) => {
    setEditingName(newValue);
  };
  
  const commitNameValue = (value = editingName) => {
    const trimmed = (value || '').trim();
    const isEmpty = trimmed === '';
    // Always notify parent when empty so it can normalize to "Collection" / "Collection 1"
    if (onNameChange && (value !== lastCommittedFolderNameRef.current || isEmpty)) {
      onNameChange(isEmpty ? '' : value);
      lastCommittedFolderNameRef.current = isEmpty ? '' : value;
    }
    isExplicitlyCancelingFolderRef.current = false;
    if (onStopEditing) {
      onStopEditing();
    }
  };
  
  const cancelNameEditing = () => {
    isExplicitlyCancelingFolderRef.current = true;
    setEditingName(lastCommittedFolderNameRef.current);
    if (onStopEditing) {
      onStopEditing();
    }
  };

  const isDemo = variant === 'demo';

  const baseClass = 'folder-header';
  const combinedClassName = [
    baseClass,
    isDropdownOpen ? 'folder-header--dropdown-open' : '',
    isDemo ? 'folder-header--variant-demo' : '',
    className
  ].filter(Boolean).join(' ');

  // Close dropdown when clicking outside, scrolling, or resizing
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && 
          !dropdownRef.current.contains(event.target) &&
          moreButtonRef.current &&
          !moreButtonRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    const handleDismiss = () => {
      setIsDropdownOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);

    // Defer scroll/resize listeners by one frame to avoid catching
    // layout-triggered scroll events during the initial render
    const rafId = requestAnimationFrame(() => {
      window.addEventListener('scroll', handleDismiss, true);
      window.addEventListener('resize', handleDismiss);
    });

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleDismiss, true);
      window.removeEventListener('resize', handleDismiss);
    };
  }, [isDropdownOpen]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // No cleanup needed for manual state management

  const handleFolderToggle = () => {
    if (onToggleExpanded) {
      onToggleExpanded(!isExpanded);
    }
  };

  const handleNameEdit = useCallback(() => {
    if (onStartEditing) {
      startNameEditing();
      onStartEditing();
    }
  }, [onStartEditing, startNameEditing]);

  const handleNameSubmit = useCallback(() => {
    // Don't process blur if we're explicitly canceling
    if (isExplicitlyCancelingFolderRef.current) {
      isExplicitlyCancelingFolderRef.current = false;
      return;
    }
    commitNameValue();
  }, [commitNameValue]);

  const handleNameCancel = useCallback(() => {
    cancelNameEditing();
    if (onStopEditing) {
      onStopEditing();
    }
  }, [cancelNameEditing, onStopEditing]);

  const handleInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleNameSubmit();
      e.target.blur();
    } else if (e.key === 'Escape') {
      handleNameCancel();
      e.target.blur();
    }
  }, [handleNameSubmit, handleNameCancel]);

  const handleInputChange = useCallback((e) => {
    updateNameValue(e.target.value);
  }, [updateNameValue]);

  const handleDropdownAction = (action) => {
    setIsDropdownOpen(false);
    switch (action) {
      case 'expand-collapse':
        if (onExpandCollapse) {
          onExpandCollapse();
        } else {
          // Fallback to the same behavior as clicking the folder toggle button
          handleFolderToggle();
        }
        break;
      case 'rename':
        if (onRename) onRename();
        break;
      case 'duplicate':
        if (onDuplicate) onDuplicate();
        break;
      case 'move':
        if (onMove) onMove();
        break;
      case 'move-up':
        if (onMoveUp) onMoveUp();
        break;
      case 'move-down':
        if (onMoveDown) onMoveDown();
        break;
      case 'delete':
        if (onDelete) onDelete();
        break;
    }
  };

  const getFolderIcon = () => {
    // Simplified behavior (no hover-based changes):
    // - Expanded: Folder open
    // - Collapsed: Folder closed
    return isExpanded ? 'folder-open' : 'folder-closed';
  };

  return (
    <>
      <header className={combinedClassName} {...props}>
        <div className="folder-header__content">
          {/* Left side - Folder toggle and name */}
          <div className="folder-header__left">
            {/* Folder icon/toggle */}
            {isDemo ? (
              <div className="folder-header__icon">
                <Icon name="folder" size="medium" />
              </div>
            ) : (
              <Button
                type="ghost"
                size="medium"
                icon={<Icon name={getFolderIcon()} size="medium" />}
                onClick={handleFolderToggle}
                className="folder-header__toggle tooltip"
                data-tooltip={isExpanded ? 'Collapse' : 'Expand'}
              />
            )}

            {/* Folder name */}
            <div className="folder-header__name">
              {/* Breadcrumb path (non-editable) */}
              {parentPathNames && parentPathNames.length > 0 && (
                <span className="folder-header__breadcrumbs">
                  {parentPathNames.join('/') + '/'}
                </span>
              )}
              {isDemo ? (
                <span className="folder-header__name-text">{folderName}</span>
              ) : (
                <Input
                  ref={inputRef}
                  type="text"
                  style="ghost"
                  value={isEditing ? editingName : folderName}
                  onChange={isEditing ? handleInputChange : undefined}
                  onBlur={isEditing ? handleNameSubmit : undefined}
                  onKeyDown={isEditing ? handleInputKeyDown : undefined}
                  onClick={!isEditing ? handleNameEdit : undefined}
                  readOnly={!isEditing}
                  className={`folder-header__name-input ${isEditing ? 'folder-header__name-input--editing' : ''}`}
                />
              )}
            </div>
          </div>

          {/* Right side - Actions */}
          {!isDemo && (
          <div className="folder-header__actions">
            {/* Add color button */}
            <Button
              type="ghost"
              size="medium"
              icon={<Icon name="add" size="medium" />}
              onClick={onAddColor}
              className="folder-header__action-button tooltip"
              data-tooltip="Add color"
            />
            {/* Add folder button */}
            <Button
              type="ghost"
              size="medium"
              icon={<Icon name="create-new-folder" size="medium" />}
              onClick={onAddFolder}
              className="folder-header__action-button tooltip"
              data-tooltip="Add folder"
            />

            {/* More actions dropdown */}
            <div className="folder-header__dropdown" ref={moreButtonRef}>
              <Button
                type="ghost"
                size="medium"
                icon={<Icon name="more-vert" size="medium" />}
                onClick={(e) => {
                  if (!isDropdownOpen) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setDropdownPosition({
                      top: rect.bottom + 4,
                      left: Math.max(8, rect.right - 120)
                    });
                  }
                  setIsDropdownOpen(!isDropdownOpen);
                }}
                className="folder-header__action-button tooltip"
                data-tooltip="More"
              />
            </div>
          </div>
          )}
        </div>
      </header>

      {isDropdownOpen && createPortal(
        <DropdownMenu
          menuRef={dropdownRef}
          fixed
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          <DropdownMenu.Item onClick={() => handleDropdownAction('expand-collapse')}>
            {isExpanded ? 'Collapse' : 'Expand'}
          </DropdownMenu.Item>
          <DropdownMenu.Divider />
          <DropdownMenu.Item onClick={() => handleDropdownAction('rename')}>
            Rename
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={() => handleDropdownAction('duplicate')}>
            Duplicate
          </DropdownMenu.Item>
          <DropdownMenu.Divider />
          <DropdownMenu.Item onClick={() => handleDropdownAction('move')}>
            Move to...
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={() => handleDropdownAction('move-up')}>
            Move up
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={() => handleDropdownAction('move-down')}>
            Move down
          </DropdownMenu.Item>
          <DropdownMenu.Divider />
          <DropdownMenu.Item danger onClick={() => handleDropdownAction('delete')}>
            Delete
          </DropdownMenu.Item>
        </DropdownMenu>,
        document.body
      )}
    </>
  );
};
